import { useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Helmet } from 'react-helmet-async';
import { ExternalLink, Twitter, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Videos() {
  const embedRef = useRef(null);
  const [feedError, setFeedError] = useState(false);

  useEffect(() => {
    let timeout;
    const loadWidget = () => {
      if (window.twttr && window.twttr.widgets) {
        window.twttr.widgets.load(embedRef.current);
        timeout = setTimeout(() => {
          const iframe = embedRef.current?.querySelector('iframe');
          if (!iframe) setFeedError(true);
        }, 5000);
      } else {
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.charset = 'utf-8';
        script.onload = () => {
          if (window.twttr && window.twttr.widgets) {
            window.twttr.widgets.load(embedRef.current);
            timeout = setTimeout(() => {
              const iframe = embedRef.current?.querySelector('iframe');
              if (!iframe) setFeedError(true);
            }, 5000);
          }
        };
        script.onerror = () => setFeedError(true);
        document.head.appendChild(script);
      }
    };
    loadWidget();
    return () => { if (timeout) clearTimeout(timeout); };
  }, []);

  const retryLoad = () => {
    setFeedError(false);
    if (window.twttr && window.twttr.widgets) {
      window.twttr.widgets.load(embedRef.current);
      setTimeout(() => {
        const iframe = embedRef.current?.querySelector('iframe');
        if (!iframe) setFeedError(true);
      }, 5000);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Latest Posts & Video Analysis | PrimeStatLab</title>
        <meta name="description" content="Follow PrimeStatLab's latest NBA player prop analysis, picks, and video breakdowns on X/Twitter." />
        <link rel="canonical" href="https://primestatlab.com/videos" />
        <meta property="og:title" content="Latest Posts & Video Analysis | PrimeStatLab" />
        <meta property="og:url" content="https://primestatlab.com/videos" />
        <meta property="og:site_name" content="PrimeStatLab" />
      </Helmet>

      <div className="container mx-auto px-6 md:px-12 py-12" data-testid="videos-page">
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-2">
            LATEST POSTS
          </h1>
          <p className="text-muted-foreground">
            Daily picks, analysis, and breakdowns
          </p>
        </div>

        <div className="max-w-2xl space-y-8">
          {/* Follow CTA + Embedded Feed */}
          <div className="bg-card border border-border rounded-lg p-8" data-testid="twitter-cta">
            <div className="flex items-start gap-5 mb-6">
              <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-[#1DA1F2]/10 rounded-full border border-[#1DA1F2]/20">
                <Twitter className="text-[#1DA1F2]" size={28} />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-2xl font-bold mb-1">@PRIMESTATLAB</h2>
                <p className="text-muted-foreground text-sm mb-5 leading-relaxed">
                  Follow for daily NBA player prop picks, real-time analysis, and free breakdowns before every slate.
                </p>
                <div className="flex gap-3 flex-wrap">
                  <a href="https://x.com/primestatlab" target="_blank" rel="noopener noreferrer">
                    <Button className="gap-2 bg-[#1DA1F2] hover:bg-[#1DA1F2]/80 text-white font-mono text-sm uppercase tracking-wider" data-testid="follow-on-x">
                      <Twitter size={16} /> Follow on X
                    </Button>
                  </a>
                  <a href="https://x.com/primestatlab" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="gap-2 font-mono text-xs" data-testid="view-posts">
                      <ExternalLink size={14} /> View on X
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Embedded X Timeline */}
            <div ref={embedRef} data-testid="twitter-embed-timeline">
              <a 
                className="twitter-timeline"
                data-theme="dark"
                data-height="800"
                data-chrome="noheader nofooter noborders transparent"
                href="https://twitter.com/primestatlab"
              >
                Loading tweets by @primestatlab...
              </a>
            </div>

            {feedError && (
              <div className="text-center py-4 mt-4 bg-surface rounded-lg border border-border">
                <p className="text-muted-foreground text-sm mb-3">
                  Feed may take a moment to load. Click retry or view directly on X.
                </p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" size="sm" onClick={retryLoad} className="gap-1" data-testid="retry-feed-button">
                    <RefreshCw size={14} /> Retry
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="https://primestatlab.com/articles" className="bg-card border border-border rounded-lg p-5 hover:border-primary/50 transition-colors group" data-testid="link-articles">
              <h3 className="font-heading font-bold mb-1 group-hover:text-primary transition-colors">ARTICLES</h3>
              <p className="text-muted-foreground text-sm">In-depth player prop breakdowns</p>
              <ArrowRight size={14} className="mt-2 text-muted-foreground group-hover:text-primary transition-colors" />
            </a>
            <a href="https://primestatlab.com/results" className="bg-card border border-border rounded-lg p-5 hover:border-primary/50 transition-colors group" data-testid="link-results">
              <h3 className="font-heading font-bold mb-1 group-hover:text-primary transition-colors">RESULTS</h3>
              <p className="text-muted-foreground text-sm">Transparent win/loss record</p>
              <ArrowRight size={14} className="mt-2 text-muted-foreground group-hover:text-primary transition-colors" />
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
